import React from 'react';
import { makeStyles } from '@material-ui/core/styles';

import { REDESIGN_COLORS } from '../../constants';
import { fmtDate, fmtNumber } from './adminFormat';

// Pure-CSS bar strip of page views (primary) and visitors (accent) per day, no
// charting library. `daily` is [{ day: 'YYYY-MM-DD', pageViews, visitors, ... }]
// from /admin/events/summary; both series share one scale (the max page views).

const HEIGHT = 140;

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: '3px',
        height: `${HEIGHT}px`,
        padding: '6px 0 0',
        borderBottom: `1px solid ${REDESIGN_COLORS.line}`,
    },
    day: {
        flex: '1 1 0',
        minWidth: 0,
        height: '100%',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        gap: '1px',
        cursor: 'default',
        '&:hover $views': {
            background: REDESIGN_COLORS.primaryHover,
        },
    },
    views: {
        flex: '1 1 0',
        minHeight: '1px',
        borderRadius: '2px 2px 0 0',
        background: REDESIGN_COLORS.primary,
    },
    visitors: {
        flex: '1 1 0',
        minHeight: '1px',
        borderRadius: '2px 2px 0 0',
        background: REDESIGN_COLORS.accent,
    },
    axis: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '6px',
        color: REDESIGN_COLORS.muted,
        fontSize: '0.74rem',
    },
    legend: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px 18px',
        marginTop: '10px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.82rem',
    },
    swatch: {
        display: 'inline-block',
        width: '10px',
        height: '10px',
        borderRadius: '2px',
        marginRight: '6px',
        verticalAlign: '-1px',
    },
    empty: {
        color: REDESIGN_COLORS.muted,
        fontSize: '0.92rem',
        margin: 0,
    },
}));

function barHeight(value, max) {
    const n = Number(value) || 0;
    if (!max || n <= 0) {
        return 0;
    }
    return Math.max(2, Math.round((n / max) * (HEIGHT - 6)));
}

// 'YYYY-MM-DD' parsed as a local date so the label does not shift a day.
function dayLabel(day) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day || '');
    if (!m) {
        return day || '';
    }
    return fmtDate(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

const DailyBars = ({ daily }) => {
    const classes = useStyles();
    const days = Array.isArray(daily) ? daily : [];
    if (days.length === 0) {
        return <p className={classes.empty}>No events recorded in this period.</p>;
    }

    const max = days.reduce((acc, d) => Math.max(acc, Number(d.pageViews) || 0, Number(d.visitors) || 0), 0);

    return (
        <div>
            <div className={classes.root} role="img" aria-label="Page views and visitors per day">
                {days.map((d) => {
                    const title = `${dayLabel(d.day)}\n${fmtNumber(d.pageViews)} page views\n${fmtNumber(
                        d.visitors
                    )} visitors\n${fmtNumber(d.sessions)} sessions\n${fmtNumber(d.events)} events`;
                    return (
                        <div key={d.day} className={classes.day} title={title}>
                            <div className={classes.views} style={{ height: `${barHeight(d.pageViews, max)}px` }} />
                            <div className={classes.visitors} style={{ height: `${barHeight(d.visitors, max)}px` }} />
                        </div>
                    );
                })}
            </div>
            <div className={classes.axis}>
                <span>{dayLabel(days[0].day)}</span>
                {days.length > 1 ? <span>{dayLabel(days[days.length - 1].day)}</span> : null}
            </div>
            <div className={classes.legend}>
                <span>
                    <span className={classes.swatch} style={{ background: REDESIGN_COLORS.primary }} />
                    Page views
                </span>
                <span>
                    <span className={classes.swatch} style={{ background: REDESIGN_COLORS.accent }} />
                    Visitors
                </span>
                <span className={classes.empty} style={{ fontSize: 'inherit' }}>
                    Hover a day for the numbers.
                </span>
            </div>
        </div>
    );
};

export default DailyBars;
